import * as React from 'react';
import {
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Link,
  styled,
  Grid,
  Popover,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from '../../../common/snackbar/SnackbarActions';
import QRCodeCanvas from 'qrcode.react';
import useMediaQuery from '@mui/material/useMediaQuery';
import GoogleShareToClassRoom from 'google-classroom-share';

const AccessCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1),
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: theme.palette.primary.main,
  borderRadius: '5px',
}));

export default function AccessLink(props) {
  const { link, name, description } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const handleOpenLinktivityClick = (event) => {
    event.preventDefault();
    window.open(link, '_blank', 'noopener');
  };

  const handleCopyToClipboardClick = (event) => {
    event.preventDefault();
    try {
      navigator.clipboard.writeText(link);
      showSuccessSnackbar('Link copied');
    } catch (err) {
      console.error(err.message);
      showErrorSnackbar('Link copy failed');
    }
  };

  const handleQRDownloadClick = (event) => {
    event.preventDefault();
    // Generate download with use canvas and stream
    const canvas = document.getElementById('linkQRCode');
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    let downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = 'qr-code.png';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const QRCodePopverStyleProps = {
    style: isSmallScreen
      ? { width: '80vw', height: '80vw' }
      : { width: '70vh', height: '70vh' },
  };

  return (
    <>
      <AccessCard elevation={0} square>
        <CardContent>
          <Grid
            container
            direction='row'
            justifyContent='center'
            alignItems='stretch'
            rowSpacing={2}
          >
            <Grid item xs={12} md={8}>
              <Stack
                direction='column'
                alignItems='left'
                justifyContent='left'
                spacing={1}
                style={{ width: '100%' }}
              >
                <Typography
                  variant='h5'
                  textAlign={'left'}
                  color='primary.main'
                >
                  Access This LINKtivity®
                </Typography>
                <Typography variant='body1' textAlign={'left'}>
                  Student Access Link
                </Typography>
                <Typography
                  variant='body2'
                  textAlign={'left'}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  <Link href={link} target='_blank' rel='noopener'>
                    {link}
                  </Link>
                </Typography>
                <Typography variant='body2' textAlign={'left'}>
                  This link is unique to your subscription, share it only with
                  your students for them to access this LINKtivity®.
                </Typography>
                <Grid
                  container
                  direction='row'
                  justifyContent='flex-start'
                  alignItems='flex-start'
                  rowSpacing={1}
                >
                  <Grid item xs={12} lg={5} style={{ textAlign: 'left' }}>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={handleCopyToClipboardClick}
                    >
                      <ContentCopyIcon style={{ marginRight: '5' }} />
                      COPY STUDENT LINK
                    </Button>
                  </Grid>
                  <Grid item xs={12} lg={5} style={{ textAlign: 'left' }}>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleOpenLinktivityClick}
                    >
                      <OpenInNewIcon style={{ marginRight: '5' }} />
                      OPEN LINKTIVITY
                    </Button>
                  </Grid>
                </Grid>
                <Typography variant='body1' textAlign={'left'} paddingTop={1}>
                  Share to Google Classroom
                </Typography>
                {link && (
                  <Grid
                    container
                    direction='row'
                    justifyContent='flex-start'
                    alignItems='flex-start'
                    rowSpacing={0}
                  >
                    <Grid item xs={12} lg={5} style={{ textAlign: 'left' }}>
                      <GoogleShareToClassRoom
                        body={description}
                        itemType='assignment'
                        url={link}
                        size={50}
                        title={name}
                        theme='classic'
                      />
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </Grid>
            <Grid item md={1}></Grid>
            <Grid item xs={12} md={3}>
              {link && (
                <Stack
                  direction='column'
                  alignItems='left'
                  justifyContent='left'
                  spacing={1}
                  style={{ width: '100%' }}
                >
                  <Typography variant='body1' textAlign={'left'}>
                    Student Link QR Code
                  </Typography>
                  <QRCodeCanvas
                    id='linkQRCode'
                    value={link}
                    style={{ width: '70%', height: 'auto', maxWidth: '200px' }}
                    onClick={handlePopoverOpen}
                    size='1024'
                  ></QRCodeCanvas>
                  <Stack
                    direction='row'
                    alignItems='left'
                    justifyContent='left'
                    spacing={0}
                  >
                    <Button
                      variant='text'
                      color='black'
                      size='small'
                      onClick={handleQRDownloadClick}
                    >
                      <DownloadIcon />
                    </Button>
                    <Button
                      variant='text'
                      color='black'
                      size='small'
                      onClick={handlePopoverOpen}
                    >
                      <OpenInFullIcon />
                    </Button>
                  </Stack>
                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    onClick={handlePopoverClose}
                    anchorReference='anchorPosition'
                    anchorPosition={{ top: 0, left: 0 }}
                    PaperProps={{
                      style: {
                        width: '100%',
                        height: '100%',
                      },
                    }}
                    disableScrollLock={true}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      <QRCodeCanvas
                        value={link}
                        size='1024'
                        {...QRCodePopverStyleProps}
                      ></QRCodeCanvas>
                    </div>
                  </Popover>
                </Stack>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </AccessCard>
    </>
  );
}
